import { webchatV2 } from '~/common/services'

/**
 * The Webchat V2 widget in **Advanced** mode, signed in as the dashboard user.
 *
 * Order: `enable_webchat_widget`, then `dashboard_session`, then the
 * `embed.js` tag. Flag off means no session call and no tag. The bearer is
 * kept only in the closure below and the tag's `data-token`, which `embed.js`
 * reads again on every `ready` (web-app-chat `public/embed.js`). It never goes
 * into Vuex, component data, storage, cookies, the URL or a log.
 */

const SCRIPT_ID = 'qontak-webchat-v2-dashboard'
/** The nodes `embed.js` adds. */
const FRAME_ID = 'qontak-webchat-widget'
const STYLE_ID = 'qontak-webchat-widget-style'
/** Within hub-core's 30 s cache-miss window, so the refresh mints a new bearer. */
const REFRESH_LEAD_MS = 15000
/** Floor, so a skewed clock or a near-dead token cannot cause a refresh loop. */
const MIN_REFRESH_MS = 30000
/** `setTimeout` fires at once above 2^31-1 ms. Staging bearers last a year. */
const MAX_TIMER_MS = 2147483647

/** The only pages that show the widget: hub `/inbox` and hub-chat-2 `/inbox-v2`. */
const WIDGET_ROUTES = ['/inbox', '/inbox-v2']

const isWidgetRoute = (path = '') =>
  WIDGET_ROUTES.some((route) => path === route || path.startsWith(`${route}/`))

const widgetOrigin = () =>
  String(process.env.HUB_EMBED_WEBCHATV2_URL || '').replace(/\/+$/, '')

const isUsable = (session) =>
  Boolean(session?.app_id && session.token?.access_token) &&
  Boolean(session.contact?.id && session.contact.is_valid) &&
  !session.contact.is_blocked

/** `null` when the call fails or the contact cannot chat. Logs no body. */
const fetchSession = async () => {
  let status = 200
  try {
    // `api/handler.js` resolves `undefined` on a network error.
    const response = await webchatV2.createDashboardSession()
    const session = response?.data?.data
    if (isUsable(session)) return session
  } catch (error) {
    status = error?.response?.status ?? null
  }
  // eslint-disable-next-line no-console
  console.warn(`[webchat-v2] dashboard session unavailable (${status})`)
  return null
}

const refreshDelay = (expiresAt) => {
  const at = Date.parse(expiresAt ?? '')
  if (Number.isNaN(at)) return null
  const delay = at - Date.now() - REFRESH_LEAD_MS
  return Math.min(Math.max(delay, MIN_REFRESH_MS), MAX_TIMER_MS)
}

const createLoaderTag = (origin, session) => {
  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.async = true
  script.src = `${origin}/embed.js`
  // The attributes embed.js `readConfig` reads.
  script.dataset.appId = session.app_id
  script.dataset.contactId = session.contact.id
  script.dataset.token = session.token.access_token
  script.dataset.shell = 'bubble'
  return script
}

/** A `ready` from our own frame, never from another window or origin. */
const isWidgetReady = (event, origin) => {
  const frame = document.getElementById(FRAME_ID)
  return (
    event.origin === origin &&
    event.source === frame?.contentWindow &&
    event.data?.eventName === 'ready'
  )
}

const removeWidgetNodes = () => {
  for (const id of [SCRIPT_ID, FRAME_ID, STYLE_ID]) {
    document.getElementById(id)?.remove()
  }
}

/**
 * The tag's lifecycle, held in one closure so the bearer has no other home.
 *
 * `run` drops a session that lands after `stop()` (logout mid-request).
 * `onMessage`: the first `ready` is the boot. A later one is the widget asking
 * again after a 401 (`recoverHostSession`). embed.js answers it at once with
 * the current `data-token`, so that refresh serves the next ask (the widget's
 * Reload button); the timer is what keeps the token fresh before it expires.
 * `stop()` also deletes `data-token`, because embed.js keeps a reference to
 * its tag. `onMountedChange` reports only a boolean, never the session.
 */
function createDashboardWidget(origin, onMountedChange) {
  let script = null
  let timer
  let run = 0
  let readyCount = 0

  const schedule = (expiresAt) => {
    clearTimeout(timer)
    const delay = refreshDelay(expiresAt)
    if (delay !== null) timer = setTimeout(refresh, delay)
  }

  async function refresh() {
    const current = run
    const session = await fetchSession()
    if (current !== run || !script || !session) return
    script.dataset.token = session.token.access_token
    schedule(session.token.expires_at)
  }

  const onMessage = (event) => {
    if (isWidgetReady(event, origin) && readyCount++ > 0) refresh()
  }

  async function start() {
    const current = run
    const session = await fetchSession()
    if (current !== run || !session) return
    script = createLoaderTag(origin, session)
    window.addEventListener('message', onMessage)
    document.body.append(script)
    onMountedChange(true)
    schedule(session.token.expires_at)
  }

  function stop() {
    run++
    readyCount = 0
    clearTimeout(timer)
    window.removeEventListener('message', onMessage)
    if (script) delete script.dataset.token
    script = null
    onMountedChange(false)
    removeWidgetNodes()
  }

  return { start, stop }
}

/**
 * Mixed into `layouts/hub.vue`, the shell of every authenticated page. Mounts
 * on the client only, and remounts whenever the owner changes: logout clears
 * `$auth.user`, an org switch changes `organization_id`, and the flag arrives
 * with the organization settings after the first render (super admins never
 * get it, and the backend refuses them). Leaving the inbox removes the widget;
 * coming back mints a new session. `isWebchatV2WidgetMounted` lets the
 * layout hide the old `QontakCustomerCare` tab, so the user sees one support
 * entry.
 */
export default {
  data() {
    return {
      isWebchatV2WidgetMounted: false,
    }
  },
  computed: {
    webchatV2WidgetOwner() {
      const flags = this.$store.state.organization?.feature_flag
      const user = this.$auth?.user
      const isOn =
        flags?.enable_webchat_widget === true &&
        isWidgetRoute(this.$route?.path)
      return isOn && widgetOrigin() && user?.id && user?.organization_id
        ? `${user.id}:${user.organization_id}`
        : ''
    },
  },
  watch: {
    webchatV2WidgetOwner: 'remountWebchatV2Widget',
  },
  mounted() {
    this.$_webchatV2Widget = createDashboardWidget(
      widgetOrigin(),
      (isMounted) => {
        this.isWebchatV2WidgetMounted = isMounted
      }
    )
    this.remountWebchatV2Widget(this.webchatV2WidgetOwner)
  },
  beforeDestroy() {
    this.$_webchatV2Widget?.stop()
  },
  methods: {
    remountWebchatV2Widget(owner) {
      if (!this.$_webchatV2Widget) return
      this.$_webchatV2Widget.stop()
      if (owner) this.$_webchatV2Widget.start()
    },
  },
}
